import { FormikErrors, yupToFormErrors } from "formik";
import {
  AccountPayer,
  AttachmentResponse,
  BillingAddressValues,
  DocumentTypeEnum,
  ElectricityBlockValues,
  FormValues,
  GasBlockValues,
  GasOptionEnum,
  InternetBlockValues,
  PremiseAddressValues,
  Service,
} from "./types";
import {
  streetTypeOptions,
  poBoxTypes,
  premiseTypes,
} from "./FormComponents/AddressConstants";
import { PhoneNumberUtil } from "google-libphonenumber";
import dayjs from "dayjs";
import {
  ConnectionTypeEnum,
  EICStatusEnum,
  ServiceTypeEnum,
  State,
} from "../Premises/types";
import { addBusinessDays, isBefore, startOfDay } from "date-fns";
import { ObjectSchema } from "yup";
import { apiClient } from "src/api/apiService";
import {
  formDataToFormikMapper,
  premiseFormFieldMapper,
} from "src/api/apiHelper";
import { Appliance, initialAppliances } from "./FormComponents/Calculator";
import {
  ElectricityService,
  GasServices,
  IBlocksExpandedState,
  IFormState,
  InternetService,
  IOnlineAccountHolderDetails,
} from "src/context/FormContext";
import { formatDpNumber } from "./helpers";
import {
  AggregatedConnectionTypes,
  AggregatedElectricityConnectionTypes,
  AggregatedGasConnectionTypes,
} from "./EIC/Timeframes";

export const abstractServiceBlockValues = (
  values: FormValues,
  serviceInitValues:
    | ElectricityBlockValues
    | GasBlockValues
    | InternetBlockValues
): ElectricityBlockValues | GasBlockValues | InternetBlockValues =>
  Object.keys(serviceInitValues).reduce(
    (acc, key) => ({
      ...acc,
      [key]: values[key as keyof FormValues],
    }),
    {} as ElectricityBlockValues | GasBlockValues | InternetBlockValues
  );

export const getStreetTypeFromKrakenValue = (streetType: string) => {
  const newStreetType = streetTypeOptions.find(
    (s) => s.value === streetType || s.label === streetType
  );
  return newStreetType?.label || "";
};

export const getPremiseTypeFromKrakenValue = (premiseType: string) => {
  return (
    premiseTypes.find((s) => s.value === premiseType)?.label || premiseType
  );
};

export const generateAddressString = (values: PremiseAddressValues) => {
  if (
    (values["streetNumber"] && values["streetName"]) ||
    values["streetName"]
  ) {
    const decodedBuildingName = values["buildingName"]
      ? `${values["buildingName"]}, `
      : "";
    const decodedDpNumber = formatDpNumber(values["dpNumber"], values["state"]);
    const decodedBlockNumber = values["blockNumber"]
      ? `Block ${values["blockNumber"]}, `
      : "";
    const decodedSectionNumber = values["sectionNumber"]
      ? `Section ${values["sectionNumber"]}, `
      : "";
    const decodedStreetSuffix = values["streetSuffix"]
      ? `${values["streetSuffix"]}, `
      : "";
    const decodedFloorNumber = values.level ? `Floor ${values.level}, ` : "";
    const decodedUnitNumber = values.unitNumber
      ? `${
          values.premiseType
            ? getPremiseTypeFromKrakenValue(values.premiseType)
            : "Unit"
        } ${values.unitNumber}, `
      : "";
    const decodedStreetType = getStreetTypeFromKrakenValue(values.streetType);
    const decodedLotNumber = values.lotNumber
      ? `Lot ${values.lotNumber}, `
      : "";
    return `${decodedBuildingName}${decodedFloorNumber}${decodedUnitNumber}${decodedBlockNumber}${decodedSectionNumber}${decodedDpNumber}${decodedLotNumber}${values["streetNumber"]} ${values["streetName"]} ${decodedStreetType} ${decodedStreetSuffix}${values["suburb"]} ${values["state"]} ${values["postcode"]}`;
  }
  return undefined;
};

export const generateBillingAddressString = (v: BillingAddressValues) => {
  if ((v["streetNumber"] && v["streetName"]) || v["streetName"]) {
    const decodedStreetSuffix = v.streetSuffix ? `${v.streetSuffix}, ` : "";
    const decodedFloorNumber = v.level ? `Floor ${v.level}, ` : "";
    const decodedStreetType = getStreetTypeFromKrakenValue(v.streetType);
    const decodedStreetNumber = v.streetNumber ?? "";
    const decodedUnitNumberPrefix = v["premiseType"]
      ? `${getPremiseTypeFromKrakenValue(v["premiseType"])} `
      : "Unit ";
    return `${decodedFloorNumber}${
      v["unitNumber"] ? `${decodedUnitNumberPrefix}${v["unitNumber"]}, ` : ""
    }${decodedStreetNumber} ${
      v["streetName"]
    } ${decodedStreetType} ${decodedStreetSuffix}${v["suburb"]} ${v["state"]} ${
      v["postcode"]
    }`;
  } else if (v.pobox) {
    return `${poBoxTypes.find((p) => p.value === v.poboxType)?.label}${
      v.poboxNumber ? ` ${v.poboxNumber}` : ""
    } ${v["suburb"]} ${v["state"]} ${v["postcode"]}`;
  }
  return undefined;
};

export const isEmptyString = (value: string | undefined) =>
  value === undefined || value === "";

export const coesDocumentTypes = [
  DocumentTypeEnum.CERTIFICATE_OF_ELECTRICAL_SAFETY,
  DocumentTypeEnum.ADDITIONAL_CERTIFICATE_OF_ELECTRICAL_SAFETY,
];

export const mcoesDocumentTypes = [
  DocumentTypeEnum.MAINS_CERTIFICATE_OF_ELECTRICAL_SAFETY,
  DocumentTypeEnum.MAINS_CERTIFICATE_OF_ELECTRICAL_SAFETY_2,
];

export const ewrDocumentTypes = [
  DocumentTypeEnum.ELECTRICAL_WORKS_REQUEST,
  DocumentTypeEnum.ELECTRICAL_WORKS_REQUEST_2,
];
export const customerConsentFilesTypes = [
  DocumentTypeEnum.NEW_ENERGY_ACCOUNT_FORM,
];

export const shipperDocumentsFilesTypes = [DocumentTypeEnum.SHIPPER_DOCUMENTS];

export const appliancesChecklistTypes = [DocumentTypeEnum.APPLIANCE_CHECKLIST];

export const sitePlanTypes = [DocumentTypeEnum.SITE_PLAN];

export const removeSupplyDocumentTypes = [
  DocumentTypeEnum.VESI_ABOLISHMENT_FORM,
];

export const mapFiles = (
  attachmentsResponse: AttachmentResponse[],
  documentTypes: DocumentTypeEnum[]
) =>
  attachmentsResponse
    .filter((fileMeta) => documentTypes.includes(fileMeta.file_upload_type))
    .map((fileMeta) => ({
      name: fileMeta.file_name,
      attachmentId: fileMeta.id,
      fileType: fileMeta.file_upload_type,
      isUploading: false,
      progress: 100,
      link: fileMeta.link,
    }));

export const getFilesByType = (attachmentsResponse: AttachmentResponse[]) => {
  const coesFiles = mapFiles(attachmentsResponse, coesDocumentTypes);
  const mcoesFiles = mapFiles(attachmentsResponse, mcoesDocumentTypes);
  const ewrFiles = mapFiles(attachmentsResponse, ewrDocumentTypes);
  const customerConsentFiles = mapFiles(
    attachmentsResponse,
    customerConsentFilesTypes
  );
  const appliancesChecklistFiles = mapFiles(
    attachmentsResponse,
    appliancesChecklistTypes
  );
  const sitePlanFiles = mapFiles(attachmentsResponse, sitePlanTypes);
  const shipperDocumentsFiles = mapFiles(
    attachmentsResponse,
    shipperDocumentsFilesTypes
  );
  return {
    coesFiles,
    mcoesFiles,
    ewrFiles,
    customerConsentFiles,
    appliancesChecklistFiles,
    sitePlanFiles,
    shipperDocumentsFiles,
  };
};

export const accountBlockCompanyEmptyFields = {
  abn: "",
  companyName: "",
};
export const secondaryAccountEmptyFields = {
  accountSecondaryFirstName: "",
  accountSecondaryLastName: "",
  accountSecondaryDob: null,
  accountSecondaryPhone: "",
  accountSecondaryEmail: "",
};

export const poboxEmptyFields = {
  poboxNumber: "",
  poboxType: "",
};

export const billingAddressEmptyFields = {
  unitNumber: "",
  premiseType: "",
  streetNumber: "",
  streetName: "",
  level: "",
  streetSuffix: "",
  streetType: "",
};

export const actAddressEmptyFields = {
  blockNumber: "",
  sectionNumber: "",
  dpNumber: "",
};
export const nswAddressEmptyFields = {
  grannyFlat: false,
  dpNumber: "",
};

export const formikFieldsBatchUpdater = (
  setter: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => Promise<void | FormikErrors<any>>,
  newData: { [key: string]: any }
) => {
  Object.keys(newData).forEach((k) => {
    setter(`${k}`, newData[k as keyof typeof newData]);
  });
};

export const getNestedValue = <T>(values: any, name: string): T | undefined =>
  name.split(".").reduce((acc: any, key) => acc && acc[key], values) as T;

// Instantiate once and reuse — defined outside the function
const phoneNumberUtil = PhoneNumberUtil.getInstance();

// Blacklisted phone numbers in national format (no spaces)
const blacklistedSignificantNumbers = new Set([
  "412345678",
  "400123456",
  "400000000",
  "411111111",
  "422222222",
  "433333333",
  "444444444",
  "455555555",
  "466666666",
  "477777777",
  "488888888",
  "499999999",
]);

export const validPhoneNumber = (value: string) => {
  // only validate non-empty values
  if (!value) return undefined;

  try {
    const parsed = phoneNumberUtil.parse(value, "AU");
    const isValid = phoneNumberUtil.isValidNumber(parsed);
    const nationalNumber = phoneNumberUtil.getNationalSignificantNumber(parsed);
    // Check if the number is valid and not blacklisted
    return isValid && !blacklistedSignificantNumbers.has(nationalNumber);
  } catch (e) {
    return false;
  }
};

export const normalizeFileName = (originalFileName: string) => {
  const dotIndex = originalFileName.lastIndexOf(".");
  const extension = dotIndex > 0 ? originalFileName.slice(dotIndex) : "";
  const originalFileNameWithoutExtension =
    dotIndex > 0 ? originalFileName.slice(0, dotIndex) : originalFileName;

  return (
    originalFileNameWithoutExtension
      .replace(/[^a-zA-Z0-9_]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_+/, "")
      .replace(/_+$/, "") + extension
  );
};

export const formatLockupDate = (date: Date) => {
  return dayjs(date).format("DD/MM/YYYY");
};

export const parseDateString = (dateString: string) => {
  return dayjs(dateString, "DD/MM/YYYY").toDate();
};

export const mapGasOptionToConnectionType = {
  gasLine: ConnectionTypeEnum.SERVICE_CONNECTION,
  gasMeter: ConnectionTypeEnum.METER_FIX,
  both: ConnectionTypeEnum.SERVICE_CONNECTION_AND_METER_FIX,
};

const DocumentTypeLabels: { [key in DocumentTypeEnum]: string | null } = {
  [DocumentTypeEnum.ELECTRICAL_WORKS_REQUEST]: "Electrical Works Request",
  [DocumentTypeEnum.ELECTRICAL_WORKS_REQUEST_2]: null,
  [DocumentTypeEnum.CERTIFICATE_OF_ELECTRICAL_SAFETY]:
    "CoES (Certificate of electrical safety)",
  [DocumentTypeEnum.ADDITIONAL_CERTIFICATE_OF_ELECTRICAL_SAFETY]: null,
  [DocumentTypeEnum.MAINS_CERTIFICATE_OF_ELECTRICAL_SAFETY]:
    "Mains control CES",
  [DocumentTypeEnum.MAINS_CERTIFICATE_OF_ELECTRICAL_SAFETY_2]: null,
  [DocumentTypeEnum.SITE_PLAN]: "Site plan",
  [DocumentTypeEnum.PHOTOS]: "Photos",
  [DocumentTypeEnum.QUOTE]: "Quote",
  [DocumentTypeEnum.PLAN_OF_SUBDIVISION]: "Plan of Subdivision",
  [DocumentTypeEnum.NEW_ENERGY_ACCOUNT_FORM]: "Customer consent",
  [DocumentTypeEnum.HANDOVER_CONSENT_FORM]: "Close and transfer form",
  [DocumentTypeEnum.SOLAR_PV_AGREEMENT]:
    "Photovoltaic Solar Generator Connection Agreement",
  [DocumentTypeEnum.VESI_ABOLISHMENT_FORM]: "VESI Abolishment Form",
  [DocumentTypeEnum.APPLIANCE_CHECKLIST]: "Appliances checklist",
  [DocumentTypeEnum.SHIPPER_DOCUMENTS]: "Shipper Documents",
  [DocumentTypeEnum.OTHER]: "Other",
};

export const mapKeyToDocumentTypeLabel = (
  documentType: DocumentTypeEnum
): string | null => {
  return DocumentTypeLabels[documentType];
};

const ConnectionTypeLabels: { [key in ConnectionTypeEnum]: string } = {
  [ConnectionTypeEnum.PERMANENT]: "Permanent",
  [ConnectionTypeEnum.TEMPORARY]: "Temporary",
  [ConnectionTypeEnum.TEMPPERM]: "Temp in perm",
  [ConnectionTypeEnum.METER_FIX]: "Gas meter",
  [ConnectionTypeEnum.SERVICE_CONNECTION]: "Gas line",
  [ConnectionTypeEnum.SERVICE_CONNECTION_AND_METER_FIX]:
    "Gas line and gas meter",
  [ConnectionTypeEnum.CAN_RETAIL]: "Internet",
};

const elecSet = new Set([
  ConnectionTypeEnum.PERMANENT,
  ConnectionTypeEnum.TEMPORARY,
  ConnectionTypeEnum.TEMPPERM,
]);

const gasSet = new Set([
  ConnectionTypeEnum.METER_FIX,
  ConnectionTypeEnum.SERVICE_CONNECTION_AND_METER_FIX,
  ConnectionTypeEnum.SERVICE_CONNECTION,
]);

export const getAggregatedConnectionTypes = (
  connectionTypes: ConnectionTypeEnum[],
  aggregatedBy: "ELECTRICITY" | "GAS" | "BOTH"
):
  | AggregatedConnectionTypes[]
  | AggregatedElectricityConnectionTypes[]
  | AggregatedGasConnectionTypes[] => {
  switch (aggregatedBy) {
    case "ELECTRICITY":
      return Array.from(
        new Set(
          connectionTypes.map((type) => {
            if (elecSet.has(type))
              return "ELECTRICITY" as AggregatedElectricityConnectionTypes;
            return type as AggregatedElectricityConnectionTypes;
          })
        )
      );
    case "GAS":
      return Array.from(
        new Set(
          connectionTypes.map((type) => {
            if (gasSet.has(type)) return "GAS" as AggregatedGasConnectionTypes;
            return type as AggregatedGasConnectionTypes;
          })
        )
      );
    case "BOTH":
      return Array.from(
        new Set(
          connectionTypes.map((type) => {
            if (elecSet.has(type))
              return "ELECTRICITY" as AggregatedConnectionTypes;
            if (gasSet.has(type)) return "GAS" as AggregatedConnectionTypes;
            return "ELECTRICITY" as AggregatedConnectionTypes; // fallback but will never happen
          })
        )
      );
    default:
      return [];
  }
};

export const mapKeyToConnectionTypeLabel = (
  connectionType: ConnectionTypeEnum,
  elecInGeneral?: boolean
): string => {
  if (elecInGeneral && elecSet.has(connectionType)) return "Electricity";
  return ConnectionTypeLabels[connectionType];
};

export const capitalizeFirstChar = (input: string) => {
  if (!input) return input;
  return input.charAt(0).toUpperCase() + input.slice(1)?.toLowerCase();
};

export const getInstallationTypeLabel = (type: string) => {
  switch (type) {
    case "underground":
      return "Underground";
    case "underground_to_overhead_mains":
      return "Underground to overhead mains";
    case "overhead":
      return "Overhead";
    case "overhead_to_underground_mains":
      return "Overhead to underground mains";
    case "transformer_ground_level":
      return "Transformer ground level";
    case "transformer_overhead":
      return "Transformer overhead";
    default:
      return null;
  }
};

export const siteConditionsOptions = [
  { label: "Battle-axe block", value: "battle-axe-block" },
  { label: "Cliff/wall/steps > 3m high", value: "cliff-wall-steps" },
  { label: "High traffic road", value: "high-traffic-road" },
  { label: "RMS road", value: "rms-road" },
  { label: "Access > 25m", value: "access-25m" },
  { label: "Shared driveway", value: "shared-driveway" },
  { label: "Other", value: "other" },
];

export const meterLocationOptions = [
  { label: "Against front fence", value: "against-front-fence" },
  { label: "Near left side fence", value: "near-left-side-fence" },
  { label: "Against left side fence", value: "against-left-side-fence" },
  { label: "Near right side fence", value: "near-right-side-fence" },
  { label: "Against right side fence", value: "against-right-side-fence" },
  { label: "Left side of car port", value: "left-side-of-car-port" },
  { label: "Right side of car port", value: "right-side-of-car-port" },
  {
    label: "Against front left of house",
    value: "against-front-left-of-house",
  },
  {
    label: "Against front centre of house",
    value: "against-front-centre-of-house",
  },
  {
    label: "Against front right of house",
    value: "against-front-right-of-house",
  },
  { label: "Against left of house", value: "against-left-of-house" },
  { label: "Against right of house", value: "against-right-of-house" },
  { label: "Other", value: "other" },
];

export const pressureOptions = [
  {
    label: "1.10 KPA",
    value: "1.10",
  },
  {
    label: "1.38 KPA (Jemena only)",
    value: "1.38",
  },
  {
    label: "2.75 KPA",
    value: "2.75",
  },
  {
    label: "Other",
    value: "0",
  },
];

export const gasMeterTimeOptions = [
  { label: "All day", value: "allDay" },
  { label: "Morning", value: "morning" },
  { label: "Afternoon", value: "afternoon" },
];

export const gasMeterAccessOptions = [
  {
    label: "Someone will be onsite",
    value: "onsite",
  },
  {
    label: "Key is in the meter box",
    value: "meter_box",
  },
  {
    label: "Key is in the mailbox",
    value: "mailbox",
  },
];

export const isUnavailableDateForThreeWorkingDays = (date: Date) => {
  if (!date) {
    return false;
  }
  const today = startOfDay(new Date());
  const threeWorkingDaysFromNow = addBusinessDays(today, 3);
  return isBefore(date, threeWorkingDaysFromNow);
};

export const isUnavailableDateForFuture = (date: Date) => {
  if (!date) {
    return false;
  }
  const today = startOfDay(new Date());
  return isBefore(date, today);
};

export const validatePlumberLicense = async (
  licenseNumber: string
): Promise<{
  valid: boolean;
  message: string;
}> => {
  try {
    const response = await apiClient.get<{
      valid: boolean;
      message: string;
    }>(`/plumber-validation/licence/${licenseNumber}`);
    return response.data;
  } catch (err) {
    console.error("Error validating plumber license", err);
    return {
      valid: false,
      message: "Error validating plumber license",
    };
  }
};

export const validateAddress = async ({
  streetName,
  streetType,
  streetSuffix,
  suburb,
  postcode,
}: {
  streetName: string;
  streetType: string;
  streetSuffix: string;
  suburb: string;
  postcode: string;
}): Promise<{
  valid: boolean;
  message: string;
}> => {
  try {
    const response = await apiClient.post("/gas-address-validation", {
      street_name: streetName,
      street_type: streetType,
      street_suffix: streetSuffix,
      suburb,
      postcode,
    });
    return response.data;
  } catch (error) {
    console.error("Error validating address", error);
    return {
      valid: false,
      message: "Error validating address",
    };
  }
};

export const validateCseNumber = async (
  licenceNumber: string,
  cocNumber: string | null,
  swnNumber: string | null
): Promise<{
  valid: boolean;
  message: string;
}> => {
  const cseNumber = cocNumber || swnNumber;
  try {
    const response = await apiClient.get(
      `/plumber-validation/licence/${licenceNumber}/certificate/${cseNumber}`
    );
    return response.data;
  } catch (error) {
    console.error("Error validating cse number", error);
    return {
      valid: false,
      message: "Error validating cse number",
    };
  }
};

interface SubmitBlockParams {
  values: FormValues;
  setErrors: (errors: any) => void;
  validationSchema: ObjectSchema<any>;
  context: any;
  lodgeRequest: () => Promise<void>;
  gasOption: "gasLine" | "gasMeter" | "both";
}

export const submitBlock = async ({
  values,
  setErrors,
  validationSchema,
  context,
  gasOption,
  lodgeRequest,
}: SubmitBlockParams) => {
  let allErrors: any = {};
  // 1. Validate the form
  try {
    await validationSchema.validate(values, {
      abortEarly: false,
      context,
    });
  } catch (e) {
    allErrors = { ...yupToFormErrors(e) };
  }

  // 2. Validate the plumber license number against the API (if applicable)
  if (values.premiseAddress.state === "VIC" && gasOption === "gasMeter") {
    const licensedPlumber: string =
      getNestedValue(values, `${gasOption}.licensedPlumber`) ?? "";

    const licenseNumber = values.licensedPlumbers.find(
      (c) => c.id === licensedPlumber
    )?.license_number;

    if (licenseNumber) {
      const { valid, message } = await validatePlumberLicense(licenseNumber);

      if (!valid) {
        allErrors[gasOption] = {
          ...allErrors[gasOption],
          licensedPlumber: message,
        };
      }
    }
  }
  // 3. Set all errors in Formik if there are any
  setErrors(allErrors);
  // 4. If there are no errors, can proceed with form submission
  if (Object.keys(allErrors).length === 0) {
    await lodgeRequest();
  }
};

export interface GasApiValidationResultsByOption {
  validAddress?: boolean;
  validCseNumber?: boolean;
  validPlumberLicence?: boolean;
}

export type GasApiValidationResults = Partial<
  Record<GasOptionEnum, GasApiValidationResultsByOption>
>;

export const preSubmissionRequestUpdate = async (
  values: FormValues,
  gasServices: GasServices,
  setFormLoadValues: (values: FormValues) => void,
  setFormState: React.Dispatch<React.SetStateAction<any>>,
  gasOption?: "gasLine" | "gasMeter" | "both",
  gasApiValidationResults?: GasApiValidationResults,
  electricityService?: ElectricityService,
  internetService?: InternetService,
  autoSubmit: boolean = false,
  isDigitalEic: boolean = true
) => {
  try {
    const updatePremiseResponse = await apiClient.post(
      "/draft/premise",
      premiseFormFieldMapper(
        values,
        {
          electricityService: electricityService ?? undefined,
          gasServices,
          internetService: internetService ?? undefined,
        },
        gasApiValidationResults,
        false,
        gasOption,
        autoSubmit,
        isDigitalEic
      )
    );

    const mappedFormikValues = formDataToFormikMapper(
      updatePremiseResponse.data
    );
    setFormLoadValues({
      ...mappedFormikValues,
      // avoid siteSupervisors and licensedPlumbers options lost after draft saving
      siteSupervisors: values.siteSupervisors,
      licensedPlumbers: values.licensedPlumbers,
    });

    const gasServiceResponse = updatePremiseResponse.data.services.find(
      (s: Service) => s.service_type === "GAS"
    );

    if (gasServiceResponse && gasOption) {
      setFormState((prevState: any) => ({
        ...prevState,
        gasServices: {
          ...prevState.gasServices,
          [gasOption]: {
            draftId: gasServiceResponse.draft_id,
            draftVersion: gasServiceResponse.draft_version,
            requestId: gasServiceResponse.request_id,
          },
        },
      }));
    }
  } catch (e) {
    console.error("Failed to save request prior to submission:", e);
  }
};

export const lodgeGenericRequest = async (
  requestId: string | undefined,
  setDisplayingSnackbar: (snackbar: { message: string; tone: string }) => void,
  gasOption: "gasLine" | "gasMeter" | "both",
  values: FormValues,
  gasServices: GasServices,
  setFormLoadValues: (values: FormValues) => void,
  setFormState: React.Dispatch<React.SetStateAction<any>>
) => {
  try {
    await preSubmissionRequestUpdate(
      values,
      gasServices,
      setFormLoadValues,
      setFormState,
      gasOption
    );
    if (requestId) {
      await apiClient.put(`/submission/${requestId}`);
    } else {
      throw new Error("Request ID is undefined");
    }
  } catch (error) {
    setDisplayingSnackbar({
      message: "Failed to lodge request",
      tone: "caution",
    });
  }
};

export const getAccountPayerField = (type: string) => {
  switch (type) {
    case "electricity":
      return "electricityAccountPayer";
    case "gasLine":
      return "gasLine.accountPayer";
    case "gasMeter":
      return "gasMeter.accountPayer";
    case "both":
      return "both.accountPayer";
    default:
      return "electricityAccountPayer";
  }
};

export const getSiteConditionsLabels = (conditions: string[] | undefined) => {
  if (!conditions || conditions.length === 0) return "";
  return conditions
    .map(
      (condition) =>
        siteConditionsOptions.find((option) => option.value === condition)
          ?.label || condition
    )
    .join(", ");
};

export const populateTotalLoadDetails = (totalLoadDetails: Appliance[]) => {
  return initialAppliances.map((appliance) => {
    const totalLoadDetail = totalLoadDetails.find(
      (detail) => detail.name === appliance.name
    );
    return {
      ...appliance,
      ...(totalLoadDetail ? { ...totalLoadDetail } : {}),
      load: totalLoadDetail?.load ? totalLoadDetail.load : appliance.unitLoad,
    };
  });
};

export const populateTotalLoadSummary = (totalLoadDetails: Appliance[]) => {
  return totalLoadDetails
    .filter((appliance) => appliance.quantity > 0)
    .map((appliance) => {
      if (appliance.quantity === 1) {
        return appliance.name;
      } else {
        return `${appliance.name} x${appliance.quantity}`;
      }
    })
    .join(", ");
};

export const hasTotalLoadDetails = (
  totalLoadDetails: Appliance[] | undefined
) => {
  if (!totalLoadDetails) return false;
  return totalLoadDetails.some((appliance) => appliance.quantity > 0);
};

export const replaceNullString = (jsonString: string): string => {
  return jsonString.replace("null", "");
};

export const determineMeterProvider = (
  state: string,
  meterProvider: string
) => {
  if (state === State.VIC) {
    return "Origin";
  }
  if (state === State.QLD && meterProvider) {
    return meterProvider;
  }
  return "Origin";
};

export const fromHyphenatedToCamelCase = (
  text: string
): keyof IBlocksExpandedState =>
  text.replace(/-([a-z])/g, (g) =>
    g[1].toUpperCase()
  ) as keyof IBlocksExpandedState;

export const setFirstLoadExpandedBlock = (serviceTypeContainer: {
  serviceType: ServiceTypeEnum;
}): string => {
  if (serviceTypeContainer?.serviceType.includes("ELEC")) {
    return "electricityBlock";
  }
  return "";
};

export const onSidebarSelection = (
  selectedRef: React.RefObject<HTMLDivElement> | null
) => {
  selectedRef?.current?.scrollIntoView({
    behavior: "smooth",
    block: "start",
    inline: "nearest",
  });
};
export const determineInitialExpandedBlocks = (
  selectedConnectionType: ConnectionTypeEnum | null,
  electricityService: ElectricityService | undefined,
  gasServices: GasServices | undefined,
  closeAndTransfer: boolean = false
) => {
  const blocksToExpand: IBlocksExpandedState = {
    premiseBlock: true,
    accountBlock: true,
    electricityBlock: false,
    gasLineBlock: false,
    gasBothBlock: false,
    gasMeterBlock: false,
    internetBlock: false,
    closeTransferBlock: false,
  };
  if (
    selectedConnectionType === ConnectionTypeEnum.PERMANENT ||
    selectedConnectionType === ConnectionTypeEnum.TEMPORARY ||
    selectedConnectionType === ConnectionTypeEnum.TEMPPERM
  ) {
    blocksToExpand["electricityBlock"] = true;
  }

  if (selectedConnectionType === ConnectionTypeEnum.SERVICE_CONNECTION) {
    blocksToExpand["gasLineBlock"] = true;
  }

  if (selectedConnectionType === ConnectionTypeEnum.METER_FIX) {
    blocksToExpand["gasMeterBlock"] = true;
  }

  if (
    selectedConnectionType ===
    ConnectionTypeEnum.SERVICE_CONNECTION_AND_METER_FIX
  ) {
    blocksToExpand["gasBothBlock"] = true;
  }

  if (
    electricityService?.isReadOnly ||
    gasServices?.both?.isReadOnly ||
    gasServices?.gasLine?.isReadOnly ||
    gasServices?.gasMeter?.isReadOnly
  ) {
    blocksToExpand["premiseBlock"] = false;
    blocksToExpand["accountBlock"] = false;
  }
  if (!selectedConnectionType) {
    blocksToExpand["premiseBlock"] = true;
  }

  if (closeAndTransfer) {
    blocksToExpand["closeTransferBlock"] = true;
    blocksToExpand["accountBlock"] = false;
    blocksToExpand["premiseBlock"] = false;
  }

  return blocksToExpand;
};

export const determineInitialScrollPosition = (
  connectionType: ConnectionTypeEnum | null,
  refs: {
    premiseBlockRef: React.RefObject<HTMLDivElement>;
    accountBlockRef: React.RefObject<HTMLDivElement>;
    electricityBlockRef: React.RefObject<HTMLDivElement>;
    gasLineBlockRef: React.RefObject<HTMLDivElement>;
    gasBothBlockRef: React.RefObject<HTMLDivElement>;
    gasMeterBlockRef: React.RefObject<HTMLDivElement>;
    internetBlockRef: React.RefObject<HTMLDivElement>;
    closeTransferBlockRef: React.RefObject<HTMLDivElement>;
  },
  closeAndTransfer: boolean = false
) => {
  if (closeAndTransfer) {
    return refs.closeTransferBlockRef;
  }
  switch (connectionType) {
    case ConnectionTypeEnum.PERMANENT:
    case ConnectionTypeEnum.TEMPORARY:
    case ConnectionTypeEnum.TEMPPERM:
      return refs.electricityBlockRef;
    case ConnectionTypeEnum.SERVICE_CONNECTION:
      return refs.gasLineBlockRef;
    case ConnectionTypeEnum.METER_FIX:
      return refs.gasMeterBlockRef;
    case ConnectionTypeEnum.SERVICE_CONNECTION_AND_METER_FIX:
      return refs.gasBothBlockRef;
    default:
      return refs.premiseBlockRef;
  }
};

type AnyObject = Record<string, unknown>;

export const removeFieldImmutable = <T extends AnyObject>(
  obj: T,
  fieldPath: string
): T | undefined => {
  const fields = fieldPath.split(".");

  const removeField = (
    currentObj: AnyObject,
    fieldIndex: number
  ): AnyObject | undefined => {
    if (fieldIndex === fields.length - 1) {
      // Remove the target field
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [fields[fieldIndex]]: _, ...rest } = currentObj;
      return Object.keys(rest).length > 0 ? rest : undefined;
    }

    const key = fields[fieldIndex];
    if (
      key in currentObj &&
      typeof currentObj[key] === "object" &&
      currentObj[key] !== null
    ) {
      // Recursively remove the field from the nested object
      const updatedChild = removeField(
        currentObj[key] as AnyObject,
        fieldIndex + 1
      );

      if (updatedChild !== undefined) {
        return { ...currentObj, [key]: updatedChild };
      }

      // Remove the current field if the child is now empty
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [key]: _, ...rest } = currentObj;
      return Object.keys(rest).length > 0 ? rest : undefined;
    }

    return currentObj; // If the key doesn't exist, return the object unchanged
  };

  return removeField(obj, 0) as T | undefined; // Ensure the returned object matches the generic type
};

export const showUpdatePremiseAlert = (
  accountPayers: AccountPayer[],
  hasAccountId: boolean,
  existingPremise: boolean
) => {
  return (
    accountPayers.includes(AccountPayer.SOMEONE_ELSE) &&
    !hasAccountId &&
    existingPremise
  );
};

export const isAnyServiceSetToTargetEicStatus = (
  formState: IFormState,
  targetEicStatus: EICStatusEnum
): boolean => {
  return [
    formState.electricityService?.eicStatus ?? EICStatusEnum.EIC_COMM_UNUSED,
    formState.gasServices?.gasLine?.eicStatus ?? EICStatusEnum.EIC_COMM_UNUSED,
    formState.gasServices?.gasMeter?.eicStatus ?? EICStatusEnum.EIC_COMM_UNUSED,
    formState.gasServices?.both?.eicStatus ?? EICStatusEnum.EIC_COMM_UNUSED,
  ].some((eicStatus) => eicStatus === targetEicStatus);
};

export const sendEicComms = async (
  premiseId: string | null,
  requestId: string | null,
  firstName: string,
  lastName: string,
  email: string,
  successMessage: string,
  errorMessage: string
): Promise<{
  valid: boolean;
  message: string;
}> => {
  try {
    await apiClient.post(`/eic/customer-notification`, {
      premise_id: premiseId,
      request_id_being_lodged: requestId,
      first_name: firstName,
      last_name: lastName,
      email,
    });
    return {
      valid: true,
      message: successMessage,
    };
  } catch (e) {
    return {
      valid: false,
      message: errorMessage,
    };
  }
};

export const isOnlineAccountHolderDetailsUnchanged = (
  values: FormValues,
  prevAccountHolderDetails: IOnlineAccountHolderDetails
): boolean => {
  return (
    !!prevAccountHolderDetails.accountPrimaryFirstName &&
    prevAccountHolderDetails.accountPrimaryFirstName ===
      values.accountPrimaryFirstName &&
    !!prevAccountHolderDetails.accountPrimaryLastName &&
    prevAccountHolderDetails.accountPrimaryLastName ===
      values.accountPrimaryLastName &&
    !!prevAccountHolderDetails.accountPrimaryEmail &&
    prevAccountHolderDetails.accountPrimaryEmail === values.accountPrimaryEmail
  );
};
export const isDateWithinNext30Days = (date: Date) => {
  if (!date) {
    return false;
  }
  const today = startOfDay(new Date());
  const thirtyWorkingDaysFromNow = addBusinessDays(today, 30);
  return isBefore(date, thirtyWorkingDaysFromNow);
};
